import badge from "@/assets/contract-cop-badge.png";
import { LogOut, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// A capital "T" whose top crossbar IS a flashing blue/red cop light bar; stem stays chrome
const PoliceT = ({ delay }) => (
  <span
    className="relative inline-block align-baseline"
    style={{ width: "0.6em", height: "0.8em" }}
    aria-label="t"
  >
    {/* top crossbar = police light bar (two cells flash opposite colors) */}
    <span
      className="absolute left-0 top-0 flex h-[0.16em] w-full overflow-hidden rounded-[1px] shadow-[0_0_5px_rgba(0,0,0,0.55)]"
      aria-hidden="true"
    >
      <span className="animate-police-dot h-full w-1/2" style={{ animationDelay: delay }} />
      <span
        className="animate-police-dot h-full w-1/2"
        style={{ animationDelay: `calc(${delay} + 0.425s)` }}
      />
    </span>
    {/* vertical stem = chrome */}
    <span
      className="absolute left-1/2 top-[0.16em] h-[calc(100%-0.16em)] w-[0.16em] -translate-x-1/2 rounded-b-[1px]"
      style={{ background: "linear-gradient(180deg,#f8fafc 0%,#cbd5e1 45%,#94a3b8 60%,#e2e8f0 100%)" }}
      aria-hidden="true"
    />
  </span>
);

export const Header = () => {
  const { user, hasActivePass, logout } = useAuth();
  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 w-full border-b border-white/10 bg-background/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-1.5 sm:px-8 sm:py-2">
        <div className="flex items-center gap-3">
          <img
            src={badge}
            alt="Contract Cop badge"
            className="h-16 w-16 object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)] sm:h-20 sm:w-20"
          />
          <div className="flex flex-col">
            <div
              data-testid="app-wordmark"
              className="font-heading text-4xl font-700 uppercase tracking-[0.18em] text-chrome sm:text-5xl"
            >
              Con<PoliceT delay="0s" />rac<PoliceT delay="0.425s" /> Cop
            </div>
            <span
              data-testid="app-subtitle"
              className="mt-0.5 pl-0.5 font-heading text-[10px] font-600 uppercase tracking-[0.3em] text-primary/90 sm:text-xs"
            >
              …for Used Cars in Ontario
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span
            data-testid="header-tagline"
            className="font-tagline text-gold-brush hidden -rotate-2 text-2xl leading-none sm:inline-block sm:text-3xl"
            style={{ paddingRight: "0.35em", paddingBottom: "0.12em" }}
          >
            Don't Sign Until You Scan!
          </span>

        {/* No "Log in" entry point here — signing in happens only at the paywall. We just show a compact
            signed-in indicator (with pass status + sign out) once a user is authenticated. */}
        {user && (
          <div className="flex items-center gap-3">
            {user.is_admin && (
              <a
                data-testid="header-dashboard-link"
                href="/dashboard"
                className="hidden rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-700 text-foreground transition-colors hover:bg-secondary sm:inline"
              >
                Dashboard
              </a>
            )}
            {hasActivePass && (
              <span
                data-testid="header-pass-badge"
                className="hidden rounded-full bg-primary/15 px-3 py-1 text-xs font-700 text-primary ring-1 ring-primary/40 sm:inline"
              >
                Pass active
              </span>
            )}
            <div className="flex items-center gap-2 rounded-full bg-secondary/70 py-1 pl-1 pr-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/20 text-primary">
                  <User className="h-4 w-4" />
                </span>
              )}
              <span className="hidden max-w-[140px] truncate text-xs font-600 text-foreground sm:inline">
                {user.name || user.email}
              </span>
            </div>
            <button
              data-testid="header-logout-button"
              onClick={logout}
              className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
        </div>
      </div>
    </header>
  );
};
